import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
// The status vocabulary (v2 + the pre-migration values kept for display) lives
// in `./status` — import it from there, not through this module.
import type { WorkflowFallback } from './routePlans';
import type {
  ItemCondition,
  ItemDeliveryLeg,
  ItemStatus,
  ShipmentStatus,
} from './status';

/** Upload-batch progress — a job status, unrelated to the shipment lifecycle. */
export type UploadShipmentStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED';

// ─────────────────────────────────────────────────────────────────────────────
// Domain entities (ERD A.4) — numeric → number, timestamps → ISO string
// ─────────────────────────────────────────────────────────────────────────────

export interface Item {
  id: string;
  code: string;
  shipmentId: string;
  itemName: string;
  /** PO / invoice number this item belongs to. */
  invoice: string | null;
  description: string | null;
  /** Unit count. API H2H sets it from `packages[].quantity`; CSV import always 1. */
  quantity: number;
  /** Physical parcel count (dus) for this item — from the CSV `Koli` column; 1 when blank. */
  koli: number;
  weight: number;
  volume: number;
  price: number | null;
  originAddress: string;
  originLat: number;
  originLong: number;
  originH3Index: string;
  destinationAddress: string;
  destinationLat: number;
  destinationLong: number;
  destinationH3Index: string;
  status: ItemStatus;
  /**
   * Physical condition — the v2 attribute that replaced item status `DAMAGED`,
   * so a damaged parcel keeps its real custody status and the flag survives to
   * the end of the journey. `null` = never inspected/flagged.
   */
  condition?: ItemCondition | null;
  /** Retur reason code from the taxonomy (`GET /v1/reasons`), if returned. */
  returReasonCode?: string | null;
  returReasonNote?: string | null;
  /** Weight (kg) recorded by the rider when the parcel was refused. */
  returWeight?: number | null;
  /**
   * Every trip this item took, oldest first — source of truth for the
   * item↔delivery link. One `LAST_MILE` row per delivery attempt, plus
   * `RETURN_TO_*` rows for the retur legs.
   */
  legs?: ItemDeliveryLeg[];
  /** Convenience mirror of the `isActive` leg, when the BE emits it. */
  activeLeg?: ItemDeliveryLeg | null;
  scannedAt: string | null;
  assignedRiderCode: string | null;
  /**
   * Pointer to the forward (last-mile) delivery. Deprecated in v2 — read
   * `legs` instead. Still dual-written, but never re-pointed on retur, so it
   * cannot answer "which delivery is running now".
   *
   * @deprecated use `legs` / `activeLeg`
   */
  externalDeliveryUid: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
  // ── Stage-S/D/B audit columns: present in DB per ERD, but the Phase-1
  // intake projection doesn't return them. Kept optional so we can read
  // them once the dispatch/bridge stages start emitting them.
  scannedBy?: string | null;
  assignedRiderId?: number | null;
  assignedAt?: string | null;
  assignedBy?: string | null;
  bridgedAt?: string | null;
  cancelledBy?: string | null;
}

export interface ShipmentClientSnapshot {
  id: number;
  name: string;
}

/**
 * Item rollup on the shipment detail. `scanned` vs `total` is how completeness
 * is expressed in v2 — there is no "partially dispatched" status any more,
 * partial is always a count.
 */
export interface ShipmentItemsSummary {
  total: number;
  scanned: number;
  /** Sparse: only statuses actually present are keyed. */
  byStatus?: Partial<Record<ItemStatus, number>>;
}

/**
 * One file field of a milestone (IMAGE photos or a SIGNATURE) and its captured
 * URLs. A SIGNATURE carries exactly one URL once submitted.
 */
export interface ShipmentRoutePhotoField {
  fieldKey: string;
  label: string;
  /**
   * Which file type produced it. Optional: an older BE sent IMAGE fields only
   * and no type, so a missing value means IMAGE.
   */
  type?: 'IMAGE' | 'SIGNATURE';
  /** Empty until the milestone is submitted. */
  urls: string[];
}

/**
 * One field of a milestone's frozen workflow snapshot, exactly what an
 * ops-evidence submission is validated against (`validateMilestoneValues`).
 */
export interface ShipmentRouteField {
  fieldKey: string;
  label: string;
  /** IMAGE | SIGNATURE | TEXT | NUMBER. */
  type: string;
  required: boolean;
  /** Type-specific rules: `imageCount` for IMAGE, `maxLength`, `min`/`max`… */
  config: Record<string, unknown> | null;
  value: unknown;
}

/**
 * Who submitted a milestone: the rider app, or ops through the Mark completed
 * override (derived by BE from the submission's `submitted_by`).
 */
export type MilestoneSubmittedVia = 'RIDER' | 'OPS';

/** A milestone that carries at least one file (photo or signature) field. */
export interface ShipmentRouteMilestone {
  key: string;
  name: string;
  /** PENDING | COMPLETED. */
  status: string;
  submittedAt: string | null;
  /**
   * RIDER | OPS once submitted, null before. Optional: an older BE does not
   * send it, and a missing value shows no source badge.
   */
  submittedVia?: MilestoneSubmittedVia | null;
  photoFields: ShipmentRoutePhotoField[];
  /**
   * The complete frozen field list in capture order — the submission contract.
   * Optional so an older BE that sends only `photoFields` still renders.
   */
  fields?: ShipmentRouteField[];
}

/** An API failure that keeps the server's rule `code` (nest envelope `code`). */
export class ShipmentApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ShipmentApiError';
  }
}

/**
 * The route stop statuses the web knows (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §1).
 * `CANCELLED` is an Ops cancel of one stop. Any other value is unknown: shown
 * raw, never actionable.
 */
export type KnownRouteStopStatus =
  'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'SKIPPED';

/** Why a SKIPPED stop was skipped; `PICKUP_FAILED` = its only pickup failed. */
export type RouteStopSkipReason = 'PICKUP_FAILED';

export interface ShipmentRouteStop {
  id: string;
  sequence: number;
  /** PICKUP | DROP_OFF. */
  type: string;
  address: string;
  /** A known status, or an unknown future one (shown raw, no actions). */
  status: KnownRouteStopStatus | (string & {});
  arrivedAt: string | null;
  resolvedAt: string | null;
  milestones: ShipmentRouteMilestone[];
  /**
   * Why the driver failed this stop (CONTRACT-4W-STOP-FAILURE §4). Non-null
   * only for a FAILED stop that has a `STOP_FAILED` timeline row — a stop an
   * ops "Mark failed" closed is FAILED with `failure: null`. Optional so an
   * older BE that does not send it still renders.
   */
  failure?: ShipmentRouteStopFailure | null;
  /**
   * PENDING and not the return-to-origin stop. Optional, like every field
   * below: an older BE does not send them.
   */
  failable?: boolean;
  /**
   * The route's return-to-origin stop (largest-sequence DROP_OFF ≤ 300 m from
   * a PICKUP), decided by the server. Never failable, never cancellable.
   */
  isReturnToOrigin?: boolean;
  /** Why Ops cancelled the stop; non-null only on a CANCELLED stop (§7). */
  cancellation?: ShipmentRouteStopCancellation | null;
  /** A milestone of the stop was submitted — the driver has arrived. */
  opened?: boolean;
  /**
   * PENDING, not opened, not return-to-origin, route not completed/cancelled:
   * the server would accept `POST …/stops/:stopID/cancel`.
   */
  cancellable?: boolean;
  /** Set on a SKIPPED stop the server skipped for a reason (§1). */
  skipReason?: RouteStopSkipReason | null;
}

/** The driver's record of a failed stop, read off its `STOP_FAILED` row. */
export interface ShipmentRouteStopFailure {
  reasonCode: string;
  /** Master label without the code prefix, e.g. "Customer menolak barang". */
  reasonLabel: string;
  note: string | null;
  photoUrls: string[];
  failedAt: string;
  /** `rider:<id>`. */
  failedBy: string | null;
  /**
   * The driver's fail landed on a stop Ops had already cancelled; the driver
   * record wins (E26, eng review D6). Optional for an older BE.
   */
  overridesCancellation?: boolean;
}

/**
 * Why Ops cancelled a stop, read off its `STOP_CANCELLED` row
 * (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §7).
 */
export interface ShipmentRouteStopCancellation {
  reasonCode: string;
  /** Reason Master label, Indonesian, e.g. "Dikirim sendiri atau vendor lain". */
  reasonLabel: string;
  /** `CLIENT_REQUEST` | `INTERNAL_ERROR` | `OTHER`; `INTERNAL_ERROR` is not billed. */
  category: string | null;
  note: string | null;
  cancelledAt: string;
  /** `ops:<id>`. */
  cancelledBy: string | null;
}

/**
 * A DIRECT_4W vehicle-condition check (start-of-trip or end-of-trip): the fixed
 * condition photos as labelled fields (same shape as milestone photos) plus the
 * odometer reading. `null` for non-4W routes / phases not yet submitted.
 */
export interface ShipmentRouteVehicleCheck {
  /** Selfie, four sides, box, odometer — labelled, in capture order. */
  photoFields: ShipmentRoutePhotoField[];
  odometerReading: number;
  submittedAt: string | null;
}

/**
 * Driver-captured evidence for a 4W/EXIM shipment's route: per-stop milestone
 * photos plus the start/end vehicle-condition checks. Present on the shipment
 * detail only when the shipment rides a route; `null` otherwise.
 */
/** Truck as booked on the route — the Dokumen Lampiran "Kendaraan" row. */
export interface ShipmentRouteVehicle {
  plateNumber: string | null;
  unitType: string | null;
}

export interface ShipmentRoute {
  routeId: string;
  code: string;
  /** PLANNED | IN_PROGRESS | COMPLETED. */
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Frozen truck snapshot; null on routes created before it was captured. */
  vehicle: ShipmentRouteVehicle | null;
  /** Rider name off the route's snapshot — the document header needs it. */
  driverName: string | null;
  /** Rider code off the route's snapshot (driver's roster code). */
  driverCode: string | null;
  /** Vehicle condition at trip start (DIRECT_4W); null otherwise / until submitted. */
  startCheck: ShipmentRouteVehicleCheck | null;
  /** Vehicle condition at trip end (DIRECT_4W); null otherwise / until submitted. */
  endCheck: ShipmentRouteVehicleCheck | null;
  stops: ShipmentRouteStop[];
}

/**
 * One captured field on a timeline event, frozen by BE at capture time with
 * the field type that produced it — so the renderer never has to resolve the
 * workflow definition to know whether this is a photo or a number.
 *
 * `value` is ALWAYS an array, whatever the type: IMAGE is natively multi-URL,
 * and TEXT/NUMBER are boxed so there is one shape to render, not two.
 */
export interface ShipmentTimelineAttachment {
  /** Workflow field key, e.g. `ktpImage`. */
  key: string;
  /** Field label as the rider saw it. */
  name: string;
  /** IMAGE | SIGNATURE | TEXT | NUMBER. SIGNATURE `value` is one URL. */
  type: string;
  value: Array<string | number>;
}

/**
 * Known timeline event types. `STOP_FAILED` is a driver failing one route stop
 * (title "Stop <seq> gagal · <label>", note, photos as an IMAGE attachment);
 * `STOP_CANCELLED` is Ops cancelling one (title "Stop <seq> dibatalkan ·
 * <label>"). Like a milestone, neither moves the status.
 */
export type ShipmentTimelineEventType =
  | 'STATUS_CHANGE'
  | 'WORKFLOW_MILESTONE'
  | 'NOTE'
  | 'STOP_FAILED'
  | 'STOP_CANCELLED';

/**
 * `metadata` on a `STOP_FAILED` row (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §8).
 * Everything past `stopSequence` is new in P0 and optional for older rows.
 */
export interface StopFailedTimelineMetadata {
  reasonCode: string;
  reasonLabel: string;
  note: string | null;
  stopSequence: number;
  /** Client-facing label (Dash Link); not shown in Mithril. */
  externalLabel?: string | null;
  clientEventId?: string | null;
  /** When the driver recorded it (device time, clamped by the server). */
  capturedAt?: string | null;
  /** When the server received it. */
  receivedAt?: string | null;
  /** Drops skipped because this (single) pickup failed. */
  cascadedStopSequences?: number[];
  /** The fail landed on a stop Ops had cancelled (E26). */
  overridesCancellation?: boolean;
}

/** `metadata` on a `STOP_CANCELLED` row (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §8). */
export interface StopCancelledTimelineMetadata {
  reasonCode: string;
  reasonLabel: string;
  category: string | null;
  note: string | null;
  stopSequence: number;
  /** Set on a drop cancelled because its single pickup was cancelled. */
  cascadedFromStopSequence: number | null;
}

/**
 * One event on the shipment's timeline. `STATUS_CHANGE` mirrors the status
 * history (`fromStatus` → `status`); `WORKFLOW_MILESTONE` is a door milestone
 * the rider submitted, which does NOT move the status — on those, `status` is
 * simply where the shipment stood at the time and `fromStatus` is null.
 */
export interface ShipmentTimelineEvent {
  id: string;
  /** A known type, or an unknown future one (rendered generically). */
  eventType: ShipmentTimelineEventType | (string & {});
  /** The shipment status at the moment of the event — set on every event. */
  status: ShipmentStatus;
  /** Only on STATUS_CHANGE; null when the event moved nothing. */
  fromStatus: ShipmentStatus | null;
  /** Milestone name on a door event; null on a plain status change. */
  title: string | null;
  note: string | null;
  routeStopId: string | null;
  workflowId: string | null;
  workflowSubmissionId: string | null;
  milestoneKey: string | null;
  milestoneSequence: number | null;
  /** Captured fields (photos, text, numbers); `[]` when there are none. */
  attachments: ShipmentTimelineAttachment[];
  /**
   * Event-specific details: `StopFailedTimelineMetadata` on `STOP_FAILED`,
   * `StopCancelledTimelineMetadata` on `STOP_CANCELLED`. Optional: older BE
   * builds do not send it.
   */
  metadata?:
    | StopFailedTimelineMetadata
    | StopCancelledTimelineMetadata
    | Record<string, unknown>
    | null;
  /** User email, `rider:<id>`, or `system`. */
  createdBy: string | null;
  /**
   * On a milestone row: RIDER | OPS, null when unknown. Optional — a missing
   * value shows no source badge.
   */
  submittedVia?: MilestoneSubmittedVia | null;
  occurredAt: string;
}

/**
 * One end of a DIRECT_4W trip: the stop it happened at, the milestone photos
 * captured there, and that phase's vehicle-condition check. Every stop field
 * is nullable — a route may carry a start check before any stop resolves, or
 * serve a shipment that only has a drop-off.
 */
export interface Direct4WSideEvidence {
  stopId: string | null;
  sequence: number | null;
  /** PICKUP | DROP_OFF. */
  type: string | null;
  address: string | null;
  /** PENDING | COMPLETED | FAILED | CANCELLED | SKIPPED (or unknown). */
  status: string | null;
  arrivedAt: string | null;
  resolvedAt: string | null;
  /** Only milestones carrying at least one photo field; `[]` when none. */
  milestones: ShipmentRouteMilestone[];
  /** `startCheck` on the start side, `endCheck` on the end side. */
  vehicleCheck: ShipmentRouteVehicleCheck | null;
}

/**
 * The start/end evidence pair for a DIRECT_4W shipment — deliberately separate
 * from `route` (the full stop list) and from `timeline`. `null` for every
 * other shipment type and for a 4W route that has captured nothing yet.
 */
export interface Direct4WEvidence {
  routeId: string;
  routeCode: string;
  start: Direct4WSideEvidence | null;
  end: Direct4WSideEvidence | null;
}

/**
 * Delivery direction for a DIRECT_4W (EXIM) shipment. `GENERAL` is non-EXIM /
 * untagged; `null` on the shipment when no direction was ever chosen (and on
 * every non-4W row). Mirrors the backend `ShipmentExim` enum — no `OTHER`.
 */
export type ShipmentExim = 'IMPORT' | 'EXPORT' | 'GENERAL';

/**
 * One frozen additional-note: the recon field's label + captured value at
 * intake. Frozen so a later definition rename never makes history unreadable;
 * custom (ad-hoc) keys carry their own label.
 */
export interface ShipmentAdditionalNote {
  label: string;
  value: string;
  /** Set only when ops filled this note after create; absent = captured at intake. */
  filledBy?: string;
  filledAt?: string;
}

/** The official document types ops can attach to a DIRECT_4W shipment. */
export type ShipmentDocumentType =
  | 'surat_jalan'
  | 'surat_dn'
  | 'dokumen_timbang_akhir'
  | 'eir_delivery'
  | 'eir_receiving'
  | 'bl';

/**
 * One ops-uploaded official document (`shipments.official_documents[key]`).
 * Patimban slots are keyed by their type; any other client's documents are
 * keyed `doc_<name>` and carry the name ops typed in `label`
 * (write-once, PLAN-SHIPMENT-DOCS-DYNAMIC).
 */
export interface ShipmentOfficialDocument {
  label?: string;
  url: string;
  filename: string;
  contentType: string | null;
  uploadedBy: string;
  uploadedAt: string;
}

/** The hub/pitstop snapshot a shipment carries (`shipments.hub`). */
export interface ShipmentHubSnapshot {
  id: number;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  code?: string;
}

/**
 * A partner-API shipment's handover role (`shipments.anchor_role`): ORIGIN =
 * DELIVERY (goods leave the hub/pitstop), DESTINATION = RETRIEVAL (goods are
 * collected and brought to it). Null for imports, H2H and DIRECT_4W.
 */
export type ShipmentAnchorRole = 'ORIGIN' | 'DESTINATION';

/** What `POST /v1/shipments/hub:assign` did for one shipment. */
export type AssignShipmentHubOutcome =
  | 'assigned'
  | 'reassigned'
  | 'not_found'
  | 'status_locked'
  | 'no_handover'
  | 'waits_for_scan'
  | 'scanned_at_hub'
  | 'items_locked'
  | 'in_dispatch_preview'
  | 'same_hub'
  | 'other_client_pitstop';

export interface AssignShipmentsHubResult {
  pitstop: { id: number; name: string; pitstopType: string };
  results: {
    shipmentId: string;
    waybill: string | null;
    outcome: AssignShipmentHubOutcome;
  }[];
  /** CREATED shipments now AT_HUB there. */
  assigned: number;
  /** AT_HUB shipments moved there. */
  reassigned: number;
}

export interface Shipment {
  id: string;
  waybill: string;
  uploadShipmentId: string | null;
  uploadShipmentCode: string | null;
  clientId: number;
  /** Snapshot saved by BE at commit (id + name). May be absent on older rows. */
  client?: ShipmentClientSnapshot | null;
  /** Revenue stream snapshot from core — peer of `client` per BE. */
  revenueStream?: string | null;
  /** DIRECT_4W cargo tag (FCL_40FT / NON_CONTAINER / …); null for other types
   *  and for 4W shipments created before it was persisted. */
  containerType?: string | null;
  /** DIRECT_4W delivery direction (Import/Export/General). Null for non-4W rows
   *  and 4W shipments created before the tag existed. */
  exim?: ShipmentExim | null;
  /** Frozen recon notes keyed by field key ({ label, value }). Null when none
   *  were captured at intake. Detail + list both project it. */
  additionalNotes?: Record<string, ShipmentAdditionalNote> | null;
  /** Ops-uploaded official documents (Surat Jalan / EIR) keyed by doc type.
   *  Null when none uploaded. Detail only. */
  officialDocuments?: Record<string, ShipmentOfficialDocument> | null;
  bookingId: string;
  deliveryGroupId: string | null;
  serviceType: string;
  /** How the consignment is carried: DOCKING | DIRECT_2W | DIRECT_4W. */
  type?: string;
  /** Hub/pitstop the shipment is at or served by (snapshot); null until a
   *  scan, an import anchor, or a handover assignment sets it. */
  hubId?: number | null;
  hub?: ShipmentHubSnapshot | null;
  anchorRole?: ShipmentAnchorRole | null;
  senderName: string;
  senderPhone: string | null;
  receiverName: string;
  receiverPhone: string;
  // Origin (pickup) is nullable: H2H clients like ZALORA don't send it at all
  // (it's set later at inbound scan from the chosen hub), while DHL-IOH still
  // does. Destination is always geocoded upstream, so it stays required.
  originAddress: string | null;
  originLat: number | null;
  originLong: number | null;
  originH3Index: string | null;
  originPhone: string | null;
  destinationAddress: string;
  destinationLat: number;
  destinationLong: number;
  destinationH3Index: string;
  destinationPhone: string | null;
  /** Drop-off note (gate, landmark…). */
  destinationNotes?: string | null;
  /** Origin → destination lane distance in metres (sent to delivery-service
   *  at dispatch); null when not measured. */
  distance?: number | null;
  totalWeight: number;
  totalVolume: number;
  status: ShipmentStatus;
  /**
   * At least one item took the retur branch. Set alongside a `COMPLETED`
   * rollup for a mixed AWB (some delivered, some returned) — the shipment is
   * settled but not cleanly delivered.
   */
  hasReturn?: boolean;
  /** Per-status rollup of this AWB's items (detail endpoint). */
  itemsSummary?: ShipmentItemsSummary;
  externalProviderId: number;
  externalShiftId: number;
  dispatchedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  /** BE-computed shipped-item count. Detail endpoint always emits this. */
  itemCount?: number;
  /**
   * Summed `koli` across this shipment's items — emitted by list + detail from
   * the same grouped query as `itemCount`, so a list row can render
   * "2 items · 5 koli" without fetching the detail. Always >= `itemCount`.
   * Optional: older BE builds omit it.
   */
  totalKoli?: number;
  items?: Item[];
  /**
   * Driver-captured milestone photos for a 4W route. Detail endpoint only, and
   * only for shipments that ride a route (4W/EXIM); `null`/absent otherwise.
   */
  route?: ShipmentRoute | null;
  /**
   * Status transitions + door milestones in one stream, **newest first**.
   * Detail endpoint only; absent on older BE builds.
   */
  timeline?: ShipmentTimelineEvent[];
  /**
   * DIRECT_4W only: the trip's start/end evidence pair. Detail endpoint only;
   * `null` for other shipment types and on older BE builds.
   */
  direct4wEvidence?: Direct4WEvidence | null;
  createdAt: string;
  updatedAt: string;
  // Stage-B audit columns — not returned by the Phase-1 projection.
  dispatchedBy?: string | null;
  cancelledBy?: string | null;
}

export interface UploadShipment {
  id: string;
  code: string | null;
  clientId: number;
  uploadedBy: string;
  fileName: string;
  totalRows: number;
  totalShipments: number;
  totalItems: number;
  status: UploadShipmentStatus;
  errorLog: unknown | null;
  uploadedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// List / pagination
// ─────────────────────────────────────────────────────────────────────────────

// BE pagination envelope (sama dengan `ProviderListResponse` di
// `services/api/providers.ts` — konvensi `nodejs-core-service`).
export interface ListPagination {
  total: number;
  size: number;
  page: number;
  lastPage: number;
}

// BE `GET /v1/shipments` query (per SHIPMENTS-API-REFERENCE §4).
//   - `search` is a case-insensitive ILIKE match against waybill OR booking_id.
//   - `createdFrom` / `createdTo` are inclusive date bounds against created_at,
//     interpreted in the tz from the `x-client-time-zone` header (default
//     Asia/Jakarta). Send ISO date `YYYY-MM-DD` — BE handles full-day inclusivity.
export interface ShipmentListParams {
  /** Any of the ten `ShipmentStatus` values; anything else 400s (07 §2.7). */
  status?: ShipmentStatus | 'ALL';
  clientId?: number;
  hubId?: number;
  /** Filter to one delivery direction (server-side). */
  exim?: ShipmentExim;
  uploadShipmentCode?: string;
  search?: string;
  /** Partial match on the assigned driver's code OR name (server-side). */
  driver?: string;
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  limit?: number;
}

export interface ShipmentListResponse {
  data: Shipment[];
  pagination: ListPagination;
}

/**
 * `GET /v1/shipments/summary` — one bucket per status, ten of them. v2 kept
 * `partiallyDispatched` / `dispatched` and added `atHub`, `returned`, `failed`
 * (09 §2).
 *
 * `total` is the sum of all ten, so a card row that shows only some of them
 * will not add up — that is expected, not a bug. Every bucket is optional so a
 * BE that omits an empty one (or an older build) leaves the chip blank instead
 * of blowing up.
 */
export interface ShipmentSummary {
  total?: number;
  created?: number;
  atHub?: number;
  partiallyDispatched?: number;
  dispatched?: number;
  inDelivery?: number;
  inDispute?: number;
  completed?: number;
  returned?: number;
  cancelled?: number;
  failed?: number;
}

/** Summary bucket per tab, so the chips read straight off the response. */
export const SHIPMENT_SUMMARY_KEYS: Record<
  ShipmentStatus,
  keyof ShipmentSummary
> = {
  CREATED: 'created',
  AT_HUB: 'atHub',
  PARTIALLY_DISPATCHED: 'partiallyDispatched',
  DISPATCHED: 'dispatched',
  IN_DELIVERY: 'inDelivery',
  IN_DISPUTE: 'inDispute',
  COMPLETED: 'completed',
  RETURNED: 'returned',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
};

// BE `GET /v1/shipments/batches` query (per SHIPMENTS-API-REFERENCE §8).
export interface UploadShipmentListParams {
  clientId?: number;
  search?: string;
  status?: UploadShipmentStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface UploadShipmentListResponse {
  data: UploadShipment[];
  pagination: ListPagination;
}

// ─────────────────────────────────────────────────────────────────────────────
// Import preview / commit shapes (must match BE Part B.8 / C.2 exactly)
// ─────────────────────────────────────────────────────────────────────────────

export interface ImportPreviewItemRow {
  rowNumber: number;
  itemName: string;
  weight: number;
  /** Parcel count from the CSV `Koli` column; 1 when the upload leaves it blank. */
  koli: number;
}

export interface ImportPreviewShipmentRow {
  bookingId: string;
  receiverName: string;
  serviceType: string;
  destinationAddress: string;
  itemCount: number;
  totalWeight: number;
  /**
   * Sum of `koli` across ALL items of this AWB. `items` below is capped by the
   * BE preview cap, so never derive this by reducing that array — same caveat
   * as `itemCount` vs `items.length`.
   */
  totalKoli: number;
  items: ImportPreviewItemRow[];
}

// An existing AWB matched by `Provider Order ID` for this client. On commit,
// item names not yet on the shipment are appended (`appendItemCount`); rows
// whose item name already exists are skipped (`skippedItemCount`).
export interface ImportPreviewDuplicateRow {
  bookingId: string;
  existingShipmentId: string;
  existingWaybill: string;
  existingStatus: ShipmentStatus | string;
  appendItemCount: number;
  skippedItemCount: number;
}

export interface ImportPreviewError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
}

// A newly-created DRAFT address lane touched by this upload (needs ops review).
export interface ImportNewAddress {
  id: string;
  originName: string;
  destinationName: string;
}

// Coordinate-resolution rollup, present on both dry-run (summary.addresses)
// and commit (data.addresses). See docs/address/shipment-import-api-fe.md §6.
export interface ImportAddressesSummary {
  total: number; // unique origin→destination lanes processed
  resolved: number; // created + reused
  created: number; // newly resolved this run (core/Google) + persisted
  reused: number; // served from an already-stored address
  unresolved: number; // failed to resolve → surfaces as a per-row error
  draft: number; // lanes in DRAFT state (new/unconfirmed); 0 after commit
  newAddresses: ImportNewAddress[]; // detail of the DRAFT lanes
}

/** Origin echo on the dry-run summary (TRD §4.1) — `null` when mode DIRECT. */
export interface ImportPreviewOrigin {
  pitstopId: number;
  name: string;
  address: string | null;
  pitstopType: string;
}

export interface ImportPreviewSummary {
  totalRows: number;
  toCreateShipments: number; // brand-new AWBs
  toCreateItems: number;
  toAppendShipments: number; // existing AWBs that gain new items
  toAppendItems: number;
  skippedDuplicateItems: number; // (AWB, item name) already exists
  duplicates: number; // existing AWBs matched
  errors: number;
  hubResolved: number; // destinations already inside this hub's zone
  hubUnresolved: number; // destinations outside every zone → auto-attach on commit
  hubConflict: number; // destinations inside a DIFFERENT hub's zone
  addresses: ImportAddressesSummary;
  ignoredColumns: string[];
  /** 🟢 echo of the requested origin mode (older BE builds omit these two). */
  originMode?: ImportOriginMode;
  /** Echo of the anchor role (ORIGIN when omitted / older BE builds). */
  anchorRole?: ImportAnchorRole;
  origin?: ImportPreviewOrigin | null;
  /**
   * Echo of the auto-dispatch instant built from `dispatchDate`+`dispatchTime`
   * (ISO, TRD v2 §D6). Only set for the CLIENT_PITSTOP scheduled variant;
   * null for the unscheduled variant ("Tanpa Jadwal" — manual dispatch) and
   * every other mode.
   */
  dispatchScheduledAt?: string | null;
}

export interface ImportPreviewResult {
  summary: ImportPreviewSummary;
  toCreate: ImportPreviewShipmentRow[];
  duplicates: ImportPreviewDuplicateRow[];
  errors: ImportPreviewError[];
}

export interface ImportCommitResult {
  uploadShipmentId: string;
  createdShipments: number; // new AWBs created
  appendedItems: number; // items added to already-existing AWBs
  skippedDuplicateItems: number; // rows skipped: (AWB, item name) existed
  autoZonedCells: number; // out-of-zone destinations attached to the nearest hub zone
  addresses: ImportAddressesSummary;
}

export type ImportErrorCode =
  | 'IMPORT_FILE_REQUIRED'
  | 'IMPORT_FILE_TOO_LARGE'
  | 'IMPORT_FILE_INVALID'
  | 'IMPORT_TOO_MANY_ROWS'
  | 'IMPORT_TOO_MANY_ERRORS'
  | 'IMPORT_VALIDATION_FAILED'
  // Origin-mode errors (client-pitstop pickup TRD §4.1)
  | 'IMPORT_ORIGIN_MODE_INVALID'
  | 'IMPORT_ANCHOR_ROLE_INVALID'
  | 'IMPORT_ORIGIN_CONFLICT'
  | 'IMPORT_PITSTOP_REQUIRED'
  | 'IMPORT_PITSTOP_MISMATCH'
  | 'IMPORT_PITSTOP_FETCH_FAILED'
  // Scheduled-dispatch slot errors (scheduled-dispatch TRD v2 §D6). Since the
  // unscheduled variant landed, omitting BOTH fields is valid ("Tanpa Jadwal");
  // IMPORT_SCHEDULE_REQUIRED is no longer emitted (kept for older BE builds).
  | 'IMPORT_SCHEDULE_REQUIRED' // 400 — legacy: dispatchDate/dispatchTime missing (CLIENT_PITSTOP)
  | 'IMPORT_SCHEDULE_INVALID' // 400 — time not an active schedule, date on a non-active day, or only one of the pair sent
  | 'IMPORT_SCHEDULE_PASSED' // 409 — the combined instant is already in the past
  | 'UNKNOWN';

/**
 * Where the imported shipments start their journey (TRD D1/D3).
 *   - `DASH_HUB`       — client delivers to a Dash hub (existing flow).
 *   - `CLIENT_PITSTOP` — goods wait at the client's own pitstop; a scheduled
 *                        auto-dispatch sends riders to pick them up.
 *   - `DIRECT`         — origin comes from the CSV per row (UI disabled in v1).
 */
export type ImportOriginMode = 'DASH_HUB' | 'CLIENT_PITSTOP' | 'DIRECT';

/**
 * Which side the hub/pitstop anchor plays (UI: "dipakai sebagai Titik Pickup /
 * Titik Dropoff"). ORIGIN = legacy (goods depart FROM the anchor);
 * DESTINATION = retrieval (goods picked up at the CSV addresses, dropped off
 * AT the anchor). Meaningless for DIRECT.
 */
export type ImportAnchorRole = 'ORIGIN' | 'DESTINATION';

export class ShipmentImportError extends Error {
  code: ImportErrorCode;
  preview?: ImportPreviewResult;
  constructor(
    code: ImportErrorCode,
    message: string,
    preview?: ImportPreviewResult
  ) {
    super(message);
    this.code = code;
    this.preview = preview;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Create Shipment — Direct 4W (PRD_4W_EXIM_End-to-End) — request contract
// ─────────────────────────────────────────────────────────────────────────────

export type Direct4WDeliveryType =
  'SINGLE_DROP' | 'MULTI_DROP' | 'MULTI_PICKUP';
export type Direct4WStopType = 'PICKUP' | 'DROP_OFF';
/**
 * DROP_OFF intent — decides the driver-facing label at that stop:
 *   UNLOAD           → "Delivery" (unload the cargo)
 *   RETURN_CONTAINER → "Drop"     (return the empty container)
 */
export type Direct4WStopIntent = 'UNLOAD' | 'RETURN_CONTAINER';

export interface CreateDirect4WStop {
  type: Direct4WStopType;
  /** Only set for DROP_OFF stops. */
  intent?: Direct4WStopIntent;
  name: string;
  address: string;
  /** REQUIRED per stop — the user must pick a place (not just type an address). */
  latitude: number;
  longitude: number;
  contactName?: string;
  contactPhone?: string;
  /** Picked from `stopWorkflowsAPI.list` (ACTIVE, matching `type` + client). */
  workflowId?: string;
  /**
   * CR5-D3: `workflowId` came from a route-plan pin — a stale/mismatched pin
   * falls back to the slot default and is reported in `workflowFallback`
   * instead of failing the create. Omit (or false) for a hand-picked workflow
   * so a stale pick still hard-fails (`WORKFLOW_NOT_ACTIVE`), as before.
   */
  allowWorkflowFallback?: boolean;
}

/** Route's rider — REQUIRED; assigns the rider that executes the route. */
export interface CreateDirect4WRider {
  id: string;
  code?: string;
  name?: string;
  phoneNumber?: string;
}

/** Route + vehicle/driver block — "dari UJP" in the UI (editable in Phase 0). */
export interface CreateDirect4WRoute {
  originName: string;
  destinationName: string;
  km: number | null;
  plateNumber: string;
  unitType: string;
  driverName: string;
  serviceType: string;
  shift: string;
}

export interface CreateDirect4WPayload {
  type: 'DIRECT_4W';
  bookingId: string;
  clientID: number;
  deliveryType: Direct4WDeliveryType;
  /** Free-form container tag (e.g. FCL_20FT / FCL_40FT / LAINNYA / NON_CONTAINER). */
  containerType: string;
  /** Delivery direction. Omit → server defaults GENERAL. Drives which required
   *  additional-note fields apply and the resolved default workflow. */
  exim?: ShipmentExim;
  /** Raw `{ key: value }` recon notes from the dynamic form. The server
   *  validates required fields for `exim` and freezes each value with its
   *  label; undefined (ad-hoc) keys pass through, bounded. */
  additionalNotes?: Record<string, string>;
  /** Human labels for the notes (key → label) so the server freezes a real
   *  label (e.g. "BL Number") instead of the snake_case key. */
  additionalNoteLabels?: Record<string, string>;
  /** ISO date (YYYY-MM-DD) of the planned delivery. */
  deliveryDate: string;
  importedFromUjp: boolean;
  /** REQUIRED — assigns the route's rider. */
  rider: CreateDirect4WRider;
  route: CreateDirect4WRoute;
  stops: CreateDirect4WStop[];
  /** CR-5: the route plan the stops were prefilled from ("Isi dari rute") — trace only. */
  routePlanId?: string;
}

/**
 * Response entity from `POST /v1/shipments/direct-4w`. `status` distinguishes a
 * freshly-created shipment from an idempotent hit on an existing (clientID,
 * bookingId).
 */
export interface CreateDirect4WResult {
  shipmentId: string;
  waybill: string;
  routeId: string;
  routeCode: string;
  status: 'CREATED' | 'EXISTS';
  /** CR-5: plan-sourced pins replaced by the slot default (1-based stop seq). */
  workflowFallback?: WorkflowFallback[];
}

// ─────────────────────────────────────────────────────────────────────────────
// OPS actions — Cancel + Force-terminal override (GET /v1/reasons taxonomy)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reason taxonomies the OPS action modals read from `GET /v1/reasons`.
 * `STOP_CANCELLATION` is the Ops "Cancel stop" list (eng review D11).
 */
export type ReasonType = 'CANCELLATION' | 'OPS_OVERRIDE' | 'STOP_CANCELLATION';

/**
 * One row from `GET /v1/reasons?type=…`. The flat pickers return
 * `{ code, label }` (already ordered by the BE) — `label` is the Indonesian
 * display text. The other fields are additive and optional
 * (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §2); older BE builds omit them.
 */
export interface ShipmentReason {
  code: string;
  label: string;
  externalLabel?: string | null;
  /** STOP_CANCELLATION: `CLIENT_REQUEST` | `INTERNAL_ERROR` | `OTHER`. */
  category?: string | null;
  searchKeywords?: string | null;
  outcome?: 'RETRY' | 'TERMINAL' | null;
  /** The reason needs a note before the server accepts it (422 NOTE_REQUIRED). */
  requiresNote?: boolean;
  requiresPhoto?: boolean;
  stopTypeScope?: 'PICKUP' | 'DROP_OFF' | 'ANY';
}

/** Body for `POST /v1/routes/:routeID/stops/:stopID/cancel` (ADMIN). */
export interface CancelStopPayload {
  reasonCode: string;
  /** Trimmed by the server, max 500; required when the reason `requiresNote`. */
  note?: string;
}

/** Response 200 of the stop cancel (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §4). */
export interface CancelStopResult {
  routeId: string;
  stopId: string;
  sequence: number;
  status: 'CANCELLED';
  reasonCode: string;
  reasonLabel: string;
  note: string | null;
  cancelledAt: string;
  /** Drops cancelled with it because their single pickup was cancelled. */
  cascadedStopIds: string[];
}

/** Body for `POST /v1/shipments/:id/cancel` — both fields optional per contract. */
export interface CancelShipmentPayload {
  reasonCode?: string;
  note?: string;
}

/** Body for `PATCH /v1/shipments/:id/destination` ("Ubah" penerima & alamat). */
export interface UpdateShipmentDestinationPayload {
  receiverName: string;
  receiverPhone: string;
  address: string;
  latitude: number;
  longitude: number;
  notes: string;
  /** Metres typed by hand; omitted → delivery-service's haversine for a
   *  moved point (shipments that carry a distance). */
  distance?: number;
}

/** COMPLETE → mark COMPLETED, FAIL → mark FAILED. */
export type ForceTerminalAction = 'COMPLETE' | 'FAIL';

/**
 * Body for `POST /v1/shipments/:id/force-terminal`. `reasonCode` +
 * `justification` are REQUIRED by the BE; `proofUrl` is optional evidence.
 */
export interface ForceTerminalPayload {
  action: ForceTerminalAction;
  reasonCode: string;
  justification: string;
  proofUrl?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Envelope helper — `{ status, message, data }` unwrap (mirror invoice)
// ─────────────────────────────────────────────────────────────────────────────

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

// Reads the JSON error message out of a blob response (export errors come back
// as a Blob because the request sets responseType: 'blob' for the download).
const parseExportBlobError = async (error: any): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || parsed.error || getBackendErrorMessage(error);
    } catch {
      return getBackendErrorMessage(error);
    }
  }
  return getBackendErrorMessage(error);
};

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const shipmentsAPI = {
  importDryRun: async (params: {
    clientId: number;
    hubId?: number | null;
    /** Origin mode; BE defaults it from `hubId` when omitted (back-compat). */
    originMode?: ImportOriginMode | null;
    /** Anchor role; omitted = ORIGIN (legacy). DESTINATION = retrieval. */
    anchorRole?: ImportAnchorRole | null;
    /** Required by the BE when `originMode === 'CLIENT_PITSTOP'`. */
    pitstopId?: number | string | null;
    /**
     * `YYYY-MM-DD` H0 — CLIENT_PITSTOP only, always as a pair with
     * `dispatchTime`: both sent = scheduled variant (auto-dispatch), both
     * omitted = unscheduled variant ("Tanpa Jadwal" — manual dispatch).
     */
    dispatchDate?: string | null;
    /** `HH:mm`, must match an active schedule of the pitstop. */
    dispatchTime?: string | null;
    file: File;
  }): Promise<ImportPreviewResult> => {
    try {
      const form = new FormData();
      form.append('clientId', String(params.clientId));
      // hubId powers the dry-run zone check (hubResolved / hubUnresolved /
      // hubConflict) — send it so the preview reflects the committed origin.
      if (params.hubId != null) form.append('hubId', String(params.hubId));
      if (params.originMode) form.append('originMode', params.originMode);
      if (params.anchorRole) form.append('anchorRole', params.anchorRole);
      if (params.pitstopId != null && params.pitstopId !== '')
        form.append('pitstopId', String(params.pitstopId));
      if (params.dispatchDate) form.append('dispatchDate', params.dispatchDate);
      if (params.dispatchTime) form.append('dispatchTime', params.dispatchTime);
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/shipments/import:dry-run', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<ImportPreviewResult>(response.data);
    } catch (error: any) {
      throw parseImportError(error);
    }
  },

  importCommit: async (params: {
    clientId: number;
    hubId?: number | null;
    originMode?: ImportOriginMode | null;
    /** Anchor role; omitted = ORIGIN (legacy). DESTINATION = retrieval. */
    anchorRole?: ImportAnchorRole | null;
    pitstopId?: number | string | null;
    /**
     * `YYYY-MM-DD` H0 — CLIENT_PITSTOP only, always as a pair with
     * `dispatchTime`: both sent = scheduled variant (auto-dispatch), both
     * omitted = unscheduled variant ("Tanpa Jadwal" — manual dispatch).
     */
    dispatchDate?: string | null;
    /** `HH:mm`, must match an active schedule of the pitstop. */
    dispatchTime?: string | null;
    file: File;
  }): Promise<ImportCommitResult> => {
    try {
      const form = new FormData();
      form.append('clientId', String(params.clientId));
      if (params.hubId != null) form.append('hubId', String(params.hubId));
      if (params.originMode) form.append('originMode', params.originMode);
      if (params.anchorRole) form.append('anchorRole', params.anchorRole);
      if (params.pitstopId != null && params.pitstopId !== '')
        form.append('pitstopId', String(params.pitstopId));
      if (params.dispatchDate) form.append('dispatchDate', params.dispatchDate);
      if (params.dispatchTime) form.append('dispatchTime', params.dispatchTime);
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/shipments/import:commit', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<ImportCommitResult>(response.data);
    } catch (error: any) {
      throw parseImportError(error);
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Create Shipment — Direct 4W (PRD_4W_EXIM_End-to-End).
  //
  // Hits the real BE endpoint `POST /v1/shipments/direct-4w` (ADMIN). The
  // endpoint lives on nest-logistic-service branch `feat/create-shipment-4w`
  // and must be merged/deployed for this to work end-to-end. It is idempotent
  // by (clientID, bookingId): a repeat call returns the existing shipment with
  // `status: 'EXISTS'` instead of creating a duplicate.
  // ───────────────────────────────────────────────────────────────────────────
  createDirect4W: async (
    payload: CreateDirect4WPayload
  ): Promise<CreateDirect4WResult> => {
    try {
      const response = await Axios.post('/v1/shipments/direct-4w', payload);
      return unwrap<CreateDirect4WResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  list: async (
    params: ShipmentListParams = {}
  ): Promise<ShipmentListResponse> => {
    try {
      const response = await Axios.get('/v1/shipments', { params });
      return unwrap<ShipmentListResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Per-status counts for the tab chips. Takes the same filters as `list`
   * minus paging, so the chips agree with the table.
   */
  summary: async (
    params: Omit<ShipmentListParams, 'page' | 'limit' | 'status'> = {}
  ): Promise<ShipmentSummary> => {
    try {
      const response = await Axios.get('/v1/shipments/summary', { params });
      return unwrap<ShipmentSummary>(response.data) ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  get: async (id: string): Promise<Shipment> => {
    try {
      const response = await Axios.get(`/v1/shipments/${id}`);
      return unwrap<Shipment>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `POST /v1/shipments/items:bulk` — several shipments with their items in one
   * round-trip, for printing many shipments' labels at once (max 100 ids; BE
   * `GetShipmentsBulkItemsRequestDto`). Lean projection: no route, evidence or
   * timeline. Unknown ids are dropped; the order of `shipmentIds` is kept.
   */
  bulkItems: async (shipmentIds: string[]): Promise<Shipment[]> => {
    try {
      const response = await Axios.post('/v1/shipments/items:bulk', {
        shipmentIds,
      });
      return unwrap<Shipment[]>(response.data) ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `POST /v1/shipments/:id/cancel` — only valid while the shipment is
   * `CREATED`. A non-CREATED shipment 400s (`INVALID_STATE`) and an unknown
   * `reasonCode` 400s (`INVALID_REASON`); both surface via `getBackendErrorMessage`.
   */
  /**
   * `POST /v1/shipments/hub:assign` — ops sets the handover hub/pitstop of
   * partner shipments the API could not resolve. One result per id.
   */
  assignHub: async (payload: {
    shipmentIds: string[];
    pitstopId: number;
  }): Promise<AssignShipmentsHubResult> => {
    try {
      const response = await Axios.post('/v1/shipments/hub:assign', payload);
      return unwrap<AssignShipmentsHubResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  cancel: async (
    id: string,
    payload: CancelShipmentPayload = {}
  ): Promise<Shipment> => {
    try {
      const response = await Axios.post(`/v1/shipments/${id}/cancel`, {
        reasonCode: payload.reasonCode ?? undefined,
        note: payload.note ?? undefined,
      });
      return unwrap<Shipment>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `PATCH /v1/shipments/:id/destination` — recipient + destination before
   * dispatch (CREATED / AT_HUB, no rider, no delivery-service delivery). The
   * BE rewrites the items (address, coordinates, H3), moves the stop in open
   * Dispatch previews and the WMS order that shipped it. 409
   * SHIPMENT_NOT_EDITABLE otherwise.
   */
  updateDestination: async (
    id: string,
    payload: UpdateShipmentDestinationPayload
  ): Promise<Shipment> => {
    try {
      const response = await Axios.patch(
        `/v1/shipments/${id}/destination`,
        payload
      );
      return unwrap<Shipment>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `POST /v1/shipments/:id/force-terminal` — the OPS Mark Completed / Mark
   * Failed override. Valid only for an in-flight shipment (AT_HUB,
   * PARTIALLY_DISPATCHED, DISPATCHED, IN_DELIVERY, IN_DISPUTE); `reasonCode` +
   * `justification` are required. CREATED / terminal 400s (`INVALID_STATE`).
   */
  forceTerminal: async (
    id: string,
    payload: ForceTerminalPayload
  ): Promise<Shipment> => {
    try {
      const response = await Axios.post(`/v1/shipments/${id}/force-terminal`, {
        action: payload.action,
        reasonCode: payload.reasonCode,
        justification: payload.justification,
        proofUrl: payload.proofUrl ?? undefined,
      });
      return unwrap<Shipment>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `POST /v1/shipments/:id/retry-delivery` — OPS retry: re-run the FAILED
   * delivery behind this shipment. Nothing changes locally on the shipment
   * from this call alone; the service resolves the delivery, delivery-service
   * re-checks eligibility and resets item/shipment/outbound synchronously.
   * Refusals (no FAILED delivery, ambiguous, not eligible) surface as the
   * backend's own message.
   */
  retryDelivery: async (
    id: string,
    payload: { reasonCode: string; notes: string }
  ): Promise<{ deliveryUid: string; deliveryStatus: string }> => {
    try {
      const response = await Axios.post(
        `/v1/shipments/${id}/retry-delivery`,
        payload
      );
      return unwrap<{ deliveryUid: string; deliveryStatus: string }>(
        response.data
      );
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `POST /v1/shipments/uploads/main` — upload one OPS override evidence photo,
   * grouped under the shipment, and get back its permanent GCS URL. The URL is
   * what goes into a milestone's `ops-evidence` values (bytes never touch the
   * override endpoint, so a rolled-back override can't strand an object).
   */
  uploadEvidence: async (shipmentId: string, file: File): Promise<string> => {
    try {
      const form = new FormData();
      form.append('shipmentId', shipmentId);
      form.append('file', file, file.name);
      const response = await Axios.post('/v1/shipments/uploads/main', form, {
        headers: { 'Content-Type': undefined },
      });
      const url = unwrap<{ imageUrl: string }>(response.data)?.imageUrl;
      if (!url) throw new Error('Upload berhasil tapi tidak mengembalikan url');
      return url;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Upload one official document (Surat Jalan / EIR — PDF or image) to GCS and
   * get its URL back. `Content-Type: undefined` lets the browser set the
   * multipart boundary (load-bearing). Commit the URL with `saveDocument`.
   */
  uploadDocument: async (shipmentId: string, file: File): Promise<string> => {
    try {
      const form = new FormData();
      form.append('shipmentId', shipmentId);
      form.append('file', file, file.name);
      const response = await Axios.post(
        '/v1/shipments/uploads/document/main',
        form,
        { headers: { 'Content-Type': undefined } }
      );
      const url = unwrap<{ fileUrl: string }>(response.data)?.fileUrl;
      if (!url) throw new Error('Upload berhasil tapi tidak mengembalikan url');
      return url;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Freeze an uploaded document URL onto the shipment
   * (`PATCH /v1/shipments/:id/documents`). Returns the merged official-documents
   * map so the caller can re-render without a refetch.
   */
  saveDocument: async (
    shipmentId: string,
    body: (
      | { docType: ShipmentDocumentType }
      // Ad-hoc (non-Patimban): the server keys it `doc_<name>` and refuses a
      // second upload under the same name (409).
      | { label: string }
    ) & {
      url: string;
      fileName: string;
      contentType?: string | null;
    }
  ): Promise<Record<string, ShipmentOfficialDocument>> => {
    try {
      const response = await Axios.patch(
        `/v1/shipments/${shipmentId}/documents`,
        body
      );
      return (
        unwrap<{ officialDocuments: Record<string, ShipmentOfficialDocument> }>(
          response.data
        )?.officialDocuments ?? {}
      );
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Fill ONE additional note after the shipment was created
   * (`PATCH /v1/shipments/:id/notes`) — e.g. the BL number on an empty return,
   * which is only known when the trip ends. Returns the merged notes map so the
   * caller can re-render without a refetch.
   *
   * WRITE-ONCE on the server: a key that already holds a value answers 409, so
   * the caller must confirm the value with ops before calling.
   */
  saveNote: async (
    shipmentId: string,
    body: { key: string; label: string; value: string }
  ): Promise<Record<string, ShipmentAdditionalNote>> => {
    try {
      const response = await Axios.patch(
        `/v1/shipments/${shipmentId}/notes`,
        body
      );
      return (
        unwrap<{ additionalNotes: Record<string, ShipmentAdditionalNote> }>(
          response.data
        )?.additionalNotes ?? {}
      );
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * `PATCH /v1/routes/:routeId/stops/:stopId/milestones/:milestoneKey/ops-evidence`
   * — attach one milestone's photos (ops override) so they render on the
   * shipment detail exactly like the rider's. `values` is keyed by field key;
   * IMAGE values are URL arrays. Call once per milestone, then force-terminal.
   */
  submitOpsMilestoneEvidence: async (
    routeId: string,
    stopId: string,
    milestoneKey: string,
    values: Record<string, unknown>
  ): Promise<void> => {
    try {
      await Axios.patch(
        `/v1/routes/${routeId}/stops/${stopId}/milestones/${milestoneKey}/ops-evidence`,
        { values }
      );
    } catch (error: any) {
      // Keep the rule code: a 409 MILESTONE_ALREADY_SUBMITTED means "already
      // saved" to the caller, not a failure.
      throw new ShipmentApiError(
        getBackendErrorMessage(error),
        error?.response?.status,
        typeof error?.response?.data?.code === 'string'
          ? error.response.data.code
          : undefined
      );
    }
  },

  /**
   * `POST /v1/routes/:routeID/stops/:stopID/cancel` — Ops cancels one PENDING
   * stop the driver has not opened yet. Keeps the rule `code` on failure so the
   * modal can tell 409 `STOP_ALREADY_OPENED` / `STOP_RESOLVED` (refresh) from a
   * 422 / 400 (show the server text).
   */
  cancelStop: async (
    routeId: string,
    stopId: string,
    payload: CancelStopPayload
  ): Promise<CancelStopResult> => {
    try {
      const response = await Axios.post(
        `/v1/routes/${routeId}/stops/${stopId}/cancel`,
        {
          reasonCode: payload.reasonCode,
          note: payload.note ?? undefined,
        }
      );
      return unwrap<CancelStopResult>(response.data);
    } catch (error: any) {
      throw new ShipmentApiError(
        getBackendErrorMessage(error),
        error?.response?.status,
        typeof error?.response?.data?.code === 'string'
          ? error.response.data.code
          : undefined
      );
    }
  },

  /**
   * `GET /v1/reasons?type=…` — the reason taxonomy behind the cancel /
   * force-terminal dropdowns. Returned sorted by `sortOrder` so the dropdown
   * order is stable regardless of BE ordering.
   */
  listReasons: async (type: ReasonType): Promise<ShipmentReason[]> => {
    try {
      const response = await Axios.get('/v1/reasons', { params: { type } });
      // The flat endpoint wraps the list: `{ reasons: [{ code, label }] }`,
      // already ordered by the BE. (It is NOT a bare array — spreading the
      // wrapper object throws "rows is not iterable".)
      const data = unwrap<{ reasons: ShipmentReason[] }>(response.data);
      return data?.reasons ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  cancelItem: async (itemId: string, reason?: string): Promise<Item> => {
    try {
      const response = await Axios.post(
        `/v1/shipments/items/${itemId}/cancel`,
        { reason: reason ?? null }
      );
      return unwrap<Item>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listUploadShipments: async (
    params: UploadShipmentListParams = {}
  ): Promise<UploadShipmentListResponse> => {
    try {
      const response = await Axios.get('/v1/shipments/batches', { params });
      return unwrap<UploadShipmentListResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getUploadShipment: async (id: string): Promise<UploadShipment> => {
    try {
      const response = await Axios.get(`/v1/shipments/batches/${id}`);
      return unwrap<UploadShipment>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  // Server-side export: hit GET /v1/shipments/export with the selected filters
  // and download the CSV the backend streams back. The backend builds the CSV
  // (one row per item, import-template columns); the client only triggers the
  // download.
  exportCSV: async (
    params: Omit<ShipmentListParams, 'page' | 'limit'> & {
      /** Explicit template override — the "custom export" flow. */
      templateKey?: string;
    }
  ): Promise<void> => {
    const query: Record<string, string | number> = {};
    if (params.templateKey) query.templateKey = params.templateKey;
    if (params.status && params.status !== 'ALL') query.status = params.status;
    if (params.clientId) query.clientId = params.clientId;
    if (params.hubId) query.hubId = params.hubId;
    if (params.exim) query.exim = params.exim;
    if (params.uploadShipmentCode)
      query.uploadShipmentCode = params.uploadShipmentCode;
    if (params.search) query.search = params.search;
    if (params.driver) query.driver = params.driver;
    if (params.createdFrom) query.createdFrom = params.createdFrom;
    if (params.createdTo) query.createdTo = params.createdTo;

    try {
      const response = await Axios.get('/v1/shipments/export', {
        params: query,
        responseType: 'blob',
      });

      // Prefer the filename the backend set via Content-Disposition.
      const disposition = response.headers['content-disposition'] as
        string | undefined;
      const match = disposition?.match(/filename="?([^"]+)"?/i);
      const filename =
        match?.[1] || `shipments-${new Date().toISOString().slice(0, 10)}.csv`;

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(await parseExportBlobError(error));
    }
  },

  /**
   * Per-(mode, client) import knobs the modal needs before upload — whether
   * Destination Lat/Long are required CSV cells for this client
   * (BE env DESTINATION_TO_CLIENT_PROVIDER_IDS).
   */
  getImportConfig: async (params: {
    originMode: ImportOriginMode;
    anchorRole?: ImportAnchorRole;
    clientId: number;
  }): Promise<{ requireDestinationCoords: boolean }> => {
    const { data } = await Axios.get('/v1/shipments/import/config', {
      params,
    });
    return unwrap(data);
  },

  /**
   * CSV template served by the BE (single source of truth — the columns can't
   * drift from what the import parser accepts). CLIENT_PITSTOP adds
   * Destination Lat/Long + Priority Time Window.
   */
  downloadTemplate: async (
    originMode: ImportOriginMode,
    anchorRole?: ImportAnchorRole
  ): Promise<void> => {
    const response = await Axios.get('/v1/shipments/import/template', {
      params: { originMode, ...(anchorRole ? { anchorRole } : {}) },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as
      string | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || 'shipment-import-template.csv';
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.setAttribute('aria-label', 'Download shipment CSV template');
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

const parseImportError = (error: any): ShipmentImportError => {
  const data = error?.response?.data;
  const code = (data?.error as ImportErrorCode) || 'UNKNOWN';
  const message =
    (data?.message as string) ||
    (typeof data?.error === 'string' ? data.error : null) ||
    getBackendErrorMessage(error);
  const preview =
    data && typeof data === 'object' && data.data && data.data.summary
      ? (data.data as ImportPreviewResult)
      : undefined;
  return new ShipmentImportError(code, message, preview);
};

// ─────────────────────────────────────────────────────────────────────────────
// Display helpers
// ─────────────────────────────────────────────────────────────────────────────

export const UPLOAD_SHIPMENT_STATUS_LABELS: Record<
  UploadShipmentStatus,
  string
> = {
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};
